import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { I18N_STORAGE_KEY, LanguageCodeEnum, type SupportedLanguage } from '@/constants/i18n'
import { i18n } from '@/i18n'
import { normalizeLanguage } from '@/utils/i18n'

/**
 * 语言配置
 */
export interface LanguageConfig {
  code: SupportedLanguage
  label: string
  nativeLabel: string
}

/**
 * 可用语言列表
 */
export const AVAILABLE_LANGUAGES: LanguageConfig[] = [
  { code: LanguageCodeEnum.Zh, label: 'Chinese', nativeLabel: '简体中文' },
  { code: LanguageCodeEnum.En, label: 'English', nativeLabel: 'English' },
]

/**
 * 全局语言切换 Hook
 * 提供语言切换、当前语言获取等功能
 * @returns 语言切换相关的状态和方法
 */
export const useLanguage = () => {
  const { i18n: i18nInstance } = useTranslation()

  /**
   * 当前语言
   */
  const currentLanguage = useMemo(() => {
    // 使用 resolvedLanguage 获取实际解析后的语言代码
    const lang = i18nInstance.resolvedLanguage || i18nInstance.language || LanguageCodeEnum.Zh
    return normalizeLanguage(lang)
  }, [i18nInstance.resolvedLanguage, i18nInstance.language])

  /**
   * 当前语言配置
   */
  const currentLanguageConfig = useMemo(() => {
    return AVAILABLE_LANGUAGES.find(lang => lang.code === currentLanguage) || AVAILABLE_LANGUAGES[0]
  }, [currentLanguage])

  /**
   * 切换语言
   * @param language - 目标语言代码
   */
  const changeLanguage = useCallback(async (language: SupportedLanguage) => {
    try {
      await i18n.changeLanguage(language)
      // 确保保存到 localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem(I18N_STORAGE_KEY, language)
      }
    } catch (error) {
      console.error('Failed to change language:', error)
    }
  }, [])

  /**
   * 切换语言（循环切换）
   */
  const toggleLanguage = useCallback(async () => {
    const currentIndex = AVAILABLE_LANGUAGES.findIndex(lang => lang.code === currentLanguage)
    const nextIndex = (currentIndex + 1) % AVAILABLE_LANGUAGES.length
    const nextLanguage = AVAILABLE_LANGUAGES[nextIndex].code
    await changeLanguage(nextLanguage)
  }, [currentLanguage, changeLanguage])

  /**
   * 检查是否为指定语言
   * @param language - 要检查的语言代码
   * @returns 是否为指定语言
   */
  const isLanguage = useCallback(
    (language: SupportedLanguage) => {
      return currentLanguage === language
    },
    [currentLanguage],
  )

  return { currentLanguage, currentLanguageConfig, availableLanguages: AVAILABLE_LANGUAGES, changeLanguage, toggleLanguage, isLanguage }
}
